import React, {
  useEffect, useMemo, useReducer, useState,
} from 'react';
import Grid from '@mui/material/Grid';
import {
  Button, FormControlLabel, List, ListItem, ListItemText, Typography,
} from '@mui/material';
import { vtrEntity } from 'shared/entityDetails';
import { BUTTON_COLORS, VARIANTS } from 'shared/constants';
import { searchNasProfiles } from 'shared/api/nases';
import { useNotification, useRequest } from 'shared/hooks';
import { AsyncSearchableSelect } from 'shared/components/select/SearchableSelects';
import { createBuildingDot1x, updateBuildingDot1x } from 'shared/api/buildings';
import { InfoModal } from 'shared/components/abstract/interactables';
import { dataReducer, DataReducerActions, initialDataReducerState } from 'shared/reducers';
import { DataFormatter } from 'shared/components/data-display/DataFormatter';
import StyledSwitch from 'shared/components/styled/switch';
import NasEditDialog from 'features/networking/complex/NasEditDialog';

/**
 * @param {Object} props
 * @param {Object} props.complex - Complex object
 * @param {number} props.updateCount - Update counter
 * @param {Function} props.triggerUpdate - Function to trigger update
 */
export default function NasPanel({
  complex,
  updateCount,
  triggerUpdate,
}) {
  const notification = useNotification();
  const { request } = useRequest();
  const [state, dispatch] = useReducer(dataReducer, {
    ...initialDataReducerState,
    data: {
      nasProfile: undefined,
      complexVtrsOnly: false,
    },
  });
  const [nonDefaultBuildings, setNonDefaultBuildings] = useState(0);

  const {
    id,
    name,
    buildings,
  } = complex;

  const {
    data,
    dirty,
  } = state;

  const {
    complexVtrsOnly,
    nasProfile,
  } = data;

  const { nas } = nasProfile || {};

  useEffect(() => {
    let count = 0;
    if (nasProfile) {
      buildings.forEach((building) => {
        if (building.dot1x?.nas_profile_id !== nasProfile.id) {
          count += 1;
        }
      });
    }
    setNonDefaultBuildings(count);
  }, [buildings, nasProfile, nonDefaultBuildings]);

  const onVTRChange = (value) => {
    dispatch({
      action: DataReducerActions.set,
      payload: {
        ...data,
        nasProfile: value,
      },
    });
  };

  const setDefault = async (updateBuildings) => {
    const reqs = [];
    updateBuildings.forEach((building) => {
      if (building.dot1x !== null && building.dot1x.building_id) {
        reqs.push(updateBuildingDot1x(building.id, {
          nas_profile_id: nasProfile.id,
        }));
      } else {
        reqs.push(createBuildingDot1x(building.id, nasProfile.id));
      }
    });

    const rowResults = await Promise.all(reqs.map((r) => request(r, 'Updated default')));
    if (rowResults) {
      notification.pushSuccess('Updated Building Nas and VTR IP');
      triggerUpdate();
      dispatch({ action: DataReducerActions.clear });
    }
    return null;
  };

  const searchRequest = useMemo(() => {
    if (complexVtrsOnly) {
      return (query) => searchNasProfiles(`${query}&complex_id=${id}`);
    }
    return searchNasProfiles;
  }, [id, complexVtrsOnly]);

  return (
    <Grid
      container
      rowSpacing={1}
      columnSpacing={{ xs: 1, sm: 2, md: 3 }}
      sx={{
        justifyContent: 'end',
      }}
    >
      <Grid size={12}>
        <AsyncSearchableSelect
          getOptionLabel={vtrEntity.getOptionLabel}
          getOptionValue={vtrEntity.getOptionLabel}
          onChange={onVTRChange}
          label="VTR"
          searchRequest={searchRequest}
          selectedOption={nasProfile}
          updateCount={updateCount}
        />
        <DataFormatter
          descriptions={[{
            label: 'Main Router',
            formatter: (n) => (n.id ? `${n.nas_name} (${n.description})` : undefined),
            readOnly: true,
          }]}
          data={nasProfile?.nas || {}}
        />
        <Grid size={12}>
          <FormControlLabel
            control={(
              <StyledSwitch
                checked={complexVtrsOnly}
                onChange={() => dispatch({
                  action: DataReducerActions.set,
                  payload: { complexVtrsOnly: !complexVtrsOnly },
                })}
                type="checkbox"
              />
            )}
            label="Common VTRs only"
          />
        </Grid>
      </Grid>
      <Grid size={{ xs: 12 }}>
        <Grid
          container
          sx={{
            justifyContent: 'flex-end',
            gap: '10px',
            paddingTop: '5px',
          }}
        >
          {dirty && (
            <Button
              variant={VARIANTS.outlined}
              disabled={!dirty}
              onClick={() => dispatch({ action: DataReducerActions.clear })}
            >
              Reset
            </Button>
          )}
          <NasEditDialog
            complex={complex}
            nasProfile={nasProfile}
            triggerUpdate={triggerUpdate}
            setDefault={setDefault}
          />
          <InfoModal
            title="Set Default VTR"
            actionName="Set Default"
            disabled={nonDefaultBuildings === 0 || !nasProfile || !nas}
            description={(
              <>
                {`By continuing, ALL building(s) in the complex ${name} not currently configured to the selected VTR will be set to the following:`}
                <List sx={{ pl: 4 }}>
                  <ListItem disablePadding key="Main Router">
                    <ListItemText
                      primary={`Main Router: ${nas?.nas_name} (${nas?.description})`}
                    />
                  </ListItem>
                  <ListItem disablePadding key="VTR">
                    <ListItemText
                      primary={`VTR: ${nasProfile?.ip}`}
                    />
                  </ListItem>
                  <ListItem disablePadding key="buildings">
                    <ListItemText
                      primary={`Affected Buildings: ${nonDefaultBuildings}`}
                    />
                  </ListItem>
                </List>
                <Typography sx={{
                  marginTop: 2,
                  fontWeight: 'bold',
                }}
                >
                  Are you sure you want to apply these changes?
                </Typography>
              </>
            )}
            actionCallback={() => setDefault(buildings)}
            color={BUTTON_COLORS.primary}
          />
        </Grid>
      </Grid>
    </Grid>
  );
}
